import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, CheckCircle, AlertCircle } from "lucide-react";
import { useLocale } from "../context/LocaleContext";

export function NewsletterSubscribe() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<
    "idle" | "loading" | "success" | "error"
  >("idle");
  const [message, setMessage] = useState("");
  const { locale } = useLocale();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("loading");
    setMessage("");

    try {
      const response = await fetch(
        `${
          process.env.REACT_APP_STRAPI_URL ||
          "https://devops.caglayagmur.dev/api"
        }/api/subscribers`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email,
            locale: locale,
          }),
        }
      );

      if (response.ok) {
        setStatus("success");
        setMessage(
          locale === "tr-TR"
            ? "Başarıyla abone oldunuz! Onay e-postasını kontrol edin."
            : "Successfully subscribed! Check your email for confirmation."
        );
        setEmail("");
      } else {
        const error = await response.json();
        setStatus("error");
        setMessage(
          error.error?.message ||
            (locale === "tr-TR"
              ? "Bu e-posta adresi zaten kayıtlı."
              : "This email is already subscribed.")
        );
      }
    } catch (error) {
      setStatus("error");
      setMessage(
        locale === "tr-TR"
          ? "Bir hata oluştu. Lütfen tekrar deneyin."
          : "An error occurred. Please try again."
      );
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-br from-gray-900 to-black border border-red-900/20 rounded-xl p-8 shadow-xl"
    >
      <div className="flex items-center gap-3 mb-4">
        <Mail className="w-6 h-6 text-red-600" />
        <h3 className="text-2xl font-bold text-white">
          {locale === "tr-TR" ? "Bültene Abone Ol" : "Subscribe to Newsletter"}
        </h3>
      </div>

      <p className="text-gray-400 mb-6">
        {locale === "tr-TR"
          ? "Yeni makaleler yayınlandığında bildirim alın. Spam yok, sadece kaliteli içerik!"
          : "Get notified when new articles are published. No spam, just quality content!"}
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={
              locale === "tr-TR" ? "E-posta adresiniz" : "Your email address"
            }
            required
            className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent transition-all"
            disabled={status === "loading" || status === "success"}
          />
        </div>

        <motion.button
          whileHover={{ scale: status === "success" ? 1 : 1.02 }}
          whileTap={{ scale: status === "success" ? 1 : 0.98 }}
          type="submit"
          disabled={status === "loading" || status === "success"}
          className={`w-full px-6 py-3 rounded-lg font-medium transition-all ${
            status === "success"
              ? "bg-green-600 text-white cursor-default"
              : "bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          }`}
        >
          {status === "loading"
            ? locale === "tr-TR"
              ? "Gönderiliyor..."
              : "Subscribing..."
            : status === "success"
            ? locale === "tr-TR"
              ? "✓ Abone Olundu"
              : "✓ Subscribed"
            : locale === "tr-TR"
            ? "Abone Ol"
            : "Subscribe"}
        </motion.button>
      </form>

      {message && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`mt-4 p-3 rounded-lg flex items-center gap-2 ${
            status === "success"
              ? "bg-green-900/20 border border-green-600/30 text-green-400"
              : "bg-red-900/20 border border-red-600/30 text-red-400"
          }`}
        >
          {status === "success" ? (
            <CheckCircle className="w-5 h-5 flex-shrink-0" />
          ) : (
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
          )}
          <p className="text-sm">{message}</p>
        </motion.div>
      )}
    </motion.div>
  );
}
